import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, Play } from "lucide-react";
import { motion } from "framer-motion";

export default function HeroSection() {
  const POSTER =
    "https://images.unsplash.com/photo-1542705959-878ca346eb20?crop=entropy&cs=srgb&fm=jpg&q=85";
  const VIDEO_SRC =
    "https://videos.pexels.com/video-files/2103099/2103099-hd_1920_1080_30fps.mp4";

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="dark-section relative min-h-screen flex flex-col overflow-hidden bg-black"
    >
      <video
        data-testid="hero-bg-video"
        autoPlay
        muted
        loop
        playsInline
        poster={POSTER}
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      >
        <source src={VIDEO_SRC} type="video/mp4" />
      </video>

      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />

      {/* Top row, eyebrow + pillars HUD, sitting safely below the navbar */}
      <div className="relative z-10 w-full px-5 md:px-10 pt-28 md:pt-32">
        <div className="mx-auto max-w-[1400px] flex items-start justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              HighwaysIndustry.com · Create · Amplify · Position
            </span>
          </motion.div>

          <div className="hidden md:block pointer-events-none">
            <div className="space-y-1 text-right">
              {["CREATE", "AMPLIFY", "POSITION"].map((p, i) => (
                <div
                  key={p}
                  className="flex items-center justify-end gap-3 font-mono text-[10px] text-accent-blue tracking-widest"
                >
                  <span>0{i + 1}</span>
                  <span>·</span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom block, headline and CTAs */}
      <div className="relative z-10 w-full px-5 md:px-10 pb-32 lg:pb-24 mt-auto">
        <div className="mx-auto max-w-[1400px]">
          <motion.h1
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="font-heading font-black text-5xl md:text-7xl lg:text-[8.5rem] leading-[0.95] tracking-tighter max-w-5xl"
          >
            Great companies
            <br />
            deserve to be{" "}
            <span className="text-accent-blue italic">seen</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed"
          >
            We create content, grow audiences and build brands across the
            highways and infrastructure sector.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: "easeOut" }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link
              to="/positioning-review"
              data-testid="hero-cta-discovery"
              className="group inline-flex items-center gap-3 bg-primary-blue text-white px-7 py-4 text-sm md:text-base font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
            >
              Book A Discovery Call
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <a
              href="#three-pillars"
              onClick={scrollTo("three-pillars")}
              data-testid="hero-cta-watch"
              className="inline-flex items-center gap-3 text-white/90 hover:text-accent-blue px-5 py-4 text-sm md:text-base font-bold uppercase tracking-widest border border-white/30 hover:border-accent-blue transition-colors rounded-full"
            >
              <Play className="h-4 w-4" />
              Watch What We Do
            </a>
          </motion.div>

          <motion.a
            href="#three-pillars"
            onClick={scrollTo("three-pillars")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-14 hidden md:inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-accent-blue label-eyebrow"
          >
            <ArrowDown className="h-4 w-4 animate-bounce" /> Scroll
          </motion.a>
        </div>
      </div>
    </section>
  );
}
